import { Component, type ErrorInfo, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { trackDeviceError } from '@/lib/deviceDiagnostics'
import { reportError } from '@/lib/errorReporting'

const CHUNK_RELOAD_KEY = 'admin-v2.chunk-reload-at'
// Allow at most one automatic reload per 30s window to avoid reload loops
// when the server keeps returning errors.
const CHUNK_RELOAD_COOLDOWN_MS = 30_000

function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|chunkloaderror|loading chunk .* failed/i.test(
    message
  )
}

/** Reload once per cooldown window; returns false when the budget is used up. */
export function reloadOnceForStaleChunk(): boolean {
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0)
    if (Date.now() - last < CHUNK_RELOAD_COOLDOWN_MS) return false
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()))
  } catch {
    // sessionStorage unavailable — still better to reload than to stay stuck
  }
  window.location.reload()
  return true
}

function ErrorFallback({ isChunkError, onRetry }: { isChunkError: boolean; onRetry: () => void }) {
  const { t } = useTranslation('common')

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center p-8">
      <div className="rounded-full bg-destructive/10 p-5 mb-4">
        <AlertTriangle className="h-10 w-10 text-destructive" />
      </div>
      <h2 className="text-lg font-semibold mb-2">
        {isChunkError ? t('errors.pageLoadTitle') : t('errors.appErrorTitle')}
      </h2>
      <p className="text-muted-foreground max-w-sm mb-5">
        {isChunkError ? t('errors.pageLoadDescription') : t('errors.appErrorDescription')}
      </p>
      <Button onClick={onRetry} variant="outline">
        <RefreshCw className="h-4 w-4 me-2" />
        {t('errors.reload')}
      </Button>
    </div>
  )
}

interface AppErrorBoundaryProps {
  children: ReactNode
  /** Change this value (e.g. the route pathname) to reset the boundary. */
  resetKey?: string
}

interface AppErrorBoundaryState {
  error: unknown
}

/**
 * Error boundary above the lazy-loaded routes.
 *
 * Its most important job: after a redeploy the content-hashed chunk files of
 * an open tab no longer exist, so navigating to a not-yet-visited page makes
 * React.lazy() reject and — without a boundary — React unmounts the whole app
 * into a permanent blank screen. We detect that case and reload the page once
 * to pick up the new build; all other errors get a visible retry UI.
 */
export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: unknown): AppErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('AppErrorBoundary caught:', error, errorInfo.componentStack)
    trackDeviceError(error, 'boundary')
    if (isChunkLoadError(error)) {
      // Stale deployment: reload to fetch the new index.html + chunks.
      reloadOnceForStaleChunk()
    } else {
      reportError(error, errorInfo.componentStack)
    }
  }

  componentDidUpdate(prevProps: AppErrorBoundaryProps) {
    if (this.state.error !== null && prevProps.resetKey !== this.props.resetKey) {
      // Navigation happened — give the new route a fresh chance to render.
      this.setState({ error: null })
    }
  }

  render() {
    if (this.state.error !== null) {
      return (
        <ErrorFallback
          isChunkError={isChunkLoadError(this.state.error)}
          onRetry={() => window.location.reload()}
        />
      )
    }
    return this.props.children
  }
}
